import { FormikErrors } from "formik";
import { LexicalEditor } from "lexical";
import _ from "lodash";
import { FeatureFlagReturnType } from "src/components/Providers/FeatureFlagProvider";
import {
  editorStateToHtmlWithCIDs,
  isEmptyLexicalState,
  serializedEditorStateToHtml,
  serializedEditorStateToPlainText,
} from "src/components/WysiwygEditor/lexical/utils/converters";
import { $validateEditorState } from "src/components/WysiwygEditor/lexical/utils/validator";
import {
  MAX_EMAIL_ATTACHMENTS_SIZE_MB,
  MB_SIZE,
  MESSAGE_CONTENT_LIMIT,
} from "src/constants";
import { toSegmentedMessage } from "src/hooks/useSegmentedMessage";
import { validateWithZod } from "src/services/formValidations";
import { mustacheSafeParse } from "src/services/mustache";
import { FormikErrorAccumulator } from "src/types/FormikErrorsAccumulator";
import { MessageContent, MessageFormSchema, MessageFormType } from "./schemas";

export const validateMessage = async (
  hasRecipientsWithEmail: boolean,
  hasRecipientsWithPhone: boolean,
  emailEditor: LexicalEditor | undefined,
  smsEditor: LexicalEditor | undefined,
  message: MessageContent,
  featureFlag: FeatureFlagReturnType
) => {
  const formikError: FormikErrors<MessageContent> = {};

  if (hasRecipientsWithEmail && !message.subject) {
    formikError.subject =
      "Email subject is required when at least one of your recipients has an email contact method.";
  }
  if (hasRecipientsWithEmail && !message.email_lexical) {
    formikError.email_lexical =
      "Email content is required when one of your recipients has an email contact method.";
  }
  if (hasRecipientsWithPhone && !message.sms_lexical) {
    formikError.sms_lexical =
      "SMS content is required when one of your recipients has an SMS contact method.";
  }

  if (message.subject) {
    const subjectError = mustacheSafeParse(
      message.subject,
      "One or more variables in the email subject line are invalid. Correct or remove them and try again."
    )[0];
    if (subjectError) formikError.subject = subjectError;
  }

  if (emailEditor && message.email_lexical) {
    const emailEditorState = emailEditor.parseEditorState(
      message.email_lexical
    );
    if (hasRecipientsWithEmail && isEmptyLexicalState(emailEditorState)) {
      formikError.email_lexical =
        "Email content is required when one of your recipients has an email contact method.";
    } else if (!$validateEditorState(emailEditorState)) {
      formikError.email_lexical =
        "One or more variables in the email content are invalid. Correct or remove them and try again.";
    } else {
      const emailHtml = serializedEditorStateToHtml(
        emailEditor,
        message.email_lexical
      );
      const lexicalError = mustacheSafeParse(
        emailHtml,
        "One or more variables in the email content are invalid. Correct or remove them and try again."
      )[0];
      if (lexicalError) formikError.email_lexical = lexicalError;
    }
  }

  if (smsEditor && message.sms_lexical) {
    const smsEditorState = smsEditor.parseEditorState(message.sms_lexical);
    const smsText =
      serializedEditorStateToPlainText(smsEditor, message.sms_lexical) ?? "";

    const isSegmentedMessageWarningEnabled =
      featureFlag["segmented_message_warning"].enabled;
    const segmentedMessage = toSegmentedMessage(smsText, featureFlag);
    if (hasRecipientsWithPhone && isEmptyLexicalState(smsEditorState)) {
      formikError.sms_lexical =
        "SMS content is required when one of your recipients has an SMS contact method.";
    } else if (
      isSegmentedMessageWarningEnabled &&
      segmentedMessage.isOverflowing()
    ) {
      formikError.sms_lexical = `SMS message exceeds character limit.`;
    } else if (
      !isSegmentedMessageWarningEnabled &&
      segmentedMessage.numberOfCharacters > MESSAGE_CONTENT_LIMIT
    ) {
      formikError.sms_lexical = `SMS message must be less than ${MESSAGE_CONTENT_LIMIT} characters.`;
    } else if (!$validateEditorState(smsEditorState)) {
      formikError.sms_lexical =
        "One or more variables in the SMS message are invalid. Correct or remove them and try again.";
    } else {
      const smsError = mustacheSafeParse(
        smsText,
        "One or more variables in the SMS message are invalid. Correct or remove them and try again."
      )[0];
      if (smsError) formikError.sms_lexical = smsError;
    }
  }
  return formikError;
};

export const validateMessages = async (
  hasRecipientsWithEmail: boolean,
  hasRecipientsWithPhone: boolean,
  emailEditor: LexicalEditor | undefined,
  smsEditor: LexicalEditor | undefined,
  values: MessageFormType,
  featureFlag: FeatureFlagReturnType,
  targetLanguages?: string[]
) => {
  const formikError = new FormikErrorAccumulator<MessageFormType>();
  for (let idx = 0; idx < values.messages.length; idx++) {
    const message = values.messages[idx];
    if (
      !message ||
      (targetLanguages && !targetLanguages.includes(message.language ?? "en"))
    ) {
      continue;
    }

    const errors = await validateMessage(
      hasRecipientsWithEmail,
      hasRecipientsWithPhone,
      emailEditor,
      smsEditor,
      message,
      featureFlag
    );
    if (Object.keys(errors).length > 0) {
      const errorMessages = values.messages.map(
        (m, i): FormikErrors<MessageContent> => (i === idx ? errors : {})
      );
      formikError.add({
        messages: errorMessages,
      });
      break;
    }
  }

  // When validating only specific languages (e.g. en for translate), skip form-level checks
  if (targetLanguages != null) {
    return { errors: formikError.errors };
  }

  let totalFileSize = 0; // This will be the sum of both inline images and attachments sizes in bytes.

  // Inline images
  if (emailEditor) {
    const htmlWithCIDs = editorStateToHtmlWithCIDs(emailEditor);
    const inlineSize =
      htmlWithCIDs?.attachments?.reduce((sum, attachment) => {
        const sizeInBase64 = attachment.data.length;
        const sizeInBytes = (sizeInBase64 * 3) / 4;
        return sum + sizeInBytes;
      }, 0) ?? 0;
    totalFileSize += inlineSize;
  }

  // Attachments
  const attachmentsSize = values.email_attachments
    ? _.sum(Array.from(values.email_attachments).map((file) => file.size))
    : 0;
  totalFileSize += attachmentsSize;

  if (totalFileSize > MAX_EMAIL_ATTACHMENTS_SIZE_MB * MB_SIZE) {
    formikError.add({
      email_attachments: `Total file size of inline images and attachments must be less than ${MAX_EMAIL_ATTACHMENTS_SIZE_MB} MB. Current total is ${(
        totalFileSize / MB_SIZE
      ).toFixed(1)}MB.`,
    });
  }

  const zodErrors = await validateWithZod(MessageFormSchema)(values);

  const allErrors = { ...formikError.errors, ...zodErrors };
  return {
    errors: allErrors,
  };
};
